import React from "react";
import { FormattedMessage } from "react-intl";

import { HStack, Text, Stack } from "@/components/ui";
import appConfig from "@/config/app-config";
import { captureException, setUserToMonitor } from "@/monitoring";
import KVStore from "@/storage/kv-store";
import sourceCode from "@/utils/code";
import { config } from "@/utils/config";
import { unregisterSw } from "@/utils/sw";

import SiteLogo from "./site-logo";

const goHome = () => (location.href = "/");

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

type State = {
  hasError: boolean;
  error: any;
  errorId?: string;
  componentStack: any;
  browser?: Bowser.Parser.Parser;
};

class ErrorBoundary extends React.PureComponent<ErrorBoundaryProps, State> {
  state: State = {
    hasError: false,
    error: undefined,
    errorId: undefined,
    componentStack: undefined,
    browser: undefined,
  };

  textarea: HTMLTextAreaElement | null = null;

  componentDidCatch(error: any, info: any): void {
    captureException(error, {
      tags: {
        // Allow page crashes to be easily searched in Sentry.
        ErrorBoundary: "yes",
      },
    }).then((errorId) => {
      this.setState({ errorId });
    });

    this.setState({
      hasError: true,
      error,
      componentStack: info && info.componentStack,
    });

    import("bowser")
      .then(({ default: Bowser }) => {
        this.setState({
          browser: Bowser.getParser(window.navigator.userAgent),
        });
      })
      .catch(() => {});
  }

  setTextareaRef: React.RefCallback<HTMLTextAreaElement> = (c) => {
    this.textarea = c;
  };

  handleCopy: React.MouseEventHandler = () => {
    if (!this.textarea) return;

    this.textarea.select();
    this.textarea.setSelectionRange(0, 99999);

    document.execCommand("copy");
  };

  getErrorText = (): string => {
    const { error, componentStack } = this.state;
    return error + componentStack;
  };

  clearCookies: React.MouseEventHandler = (e) => {
    localStorage.clear();
    sessionStorage.clear();
    KVStore.clear();
    setUserToMonitor(null);

    if ("serviceWorker" in navigator) {
      e.preventDefault();
      unregisterSw().then(goHome).catch(goHome);
    }
  };

  render() {
    const { browser, hasError, errorId } = this.state;
    const { children } = this.props;

    if (!hasError) {
      return children;
    }

    const isProduction = appConfig.NODE_ENV === "production";

    const errorText = this.getErrorText();

    return (
      <div className="flex h-screen flex-col bg-white pt-16 pb-12 dark:bg-gray-900">
        <main className="mx-auto flex w-full max-w-7xl grow flex-col justify-center px-4 sm:px-6 lg:px-8">
          <div className="flex shrink-0 justify-center">
            <a href="/" className="inline-flex">
              <SiteLogo alt="Logo" className="h-12 w-auto cursor-pointer" />
            </a>
          </div>

          <div className="py-8">
            <div className="mx-auto max-w-xl space-y-2 text-center">
              <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-500">
                <FormattedMessage
                  id="alert.unexpected.message"
                  defaultMessage="An unexpected error occurred."
                />
              </h1>
              <p className="text-lg text-muted-foreground">
                <FormattedMessage
                  id="alert.unexpected.body"
                  defaultMessage="We're sorry for the interruption. If the problem persists, please reach out to our support team. You may also try to {clearCookies} (this will log you out)."
                  values={{
                    clearCookies: (
                      <a
                        href="/"
                        onClick={this.clearCookies}
                        className="text-primary-600 hover:underline dark:text-primary-400"
                      >
                        <FormattedMessage
                          id="alert.unexpected.clear_cookies"
                          defaultMessage="clear cookies and browser data"
                        />
                      </a>
                    ),
                  }}
                />
              </p>

              <HStack alignItems="center" justifyContent="center" space={6}>
                {browser && (
                  <Stack>
                    <Text weight="semibold">
                      <FormattedMessage
                        id="alert.unexpected.browser"
                        defaultMessage="Browser"
                      />
                    </Text>
                    <Text theme="muted">
                      {browser.getBrowserName()} {browser.getBrowserVersion()}
                    </Text>
                  </Stack>
                )}

                <Stack>
                  <Text weight="semibold">
                    <FormattedMessage
                      id="alert.unexpected.version"
                      defaultMessage="Version"
                    />
                  </Text>
                  <Text theme="muted">{sourceCode.version}</Text>
                </Stack>

                {errorId && (
                  <Stack>
                    <Text weight="semibold">
                      <FormattedMessage
                        id="alert.unexpected.errorId"
                        defaultMessage="Error ID"
                      />
                    </Text>
                    <Text theme="muted">{errorId}</Text>
                  </Stack>
                )}
              </HStack>

              <div className="mt-10">
                <a
                  href="/"
                  className="text-base font-medium text-primary-600 hover:underline dark:text-primary-400"
                >
                  <FormattedMessage
                    id="alert.unexpected.return_home"
                    defaultMessage="Return Home"
                  />
                  <span aria-hidden="true"> &rarr;</span>
                </a>
              </div>
            </div>

            {!isProduction && (
              <div className="mx-auto max-w-lg space-y-4 py-16">
                {errorText && (
                  <textarea
                    ref={this.setTextareaRef}
                    className="block h-48 w-full rounded-md border bg-gray-100 p-4 font-mono text-gray-900 shadow-xs focus:border-primary-500 focus:ring-2 focus:ring-primary-500 sm:text-sm dark:bg-gray-900 dark:text-gray-100"
                    value={errorText}
                    onClick={this.handleCopy}
                    readOnly
                  />
                )}
              </div>
            )}
          </div>
        </main>

        <footer className="mx-auto w-full max-w-7xl shrink-0 px-4 sm:px-6 lg:px-8">
          <HStack justifyContent="center" space={4} element="nav">
            <a
              href={config.links.status.href}
              className="text-sm font-medium text-muted-foreground hover:underline"
            >
              <config.links.status.FormattedMessage />
            </a>

            <>
              <span className="inline-block border-l" aria-hidden="true" />
              <a
                href={config.links.help.href}
                className="text-sm font-medium text-muted-foreground hover:underline"
              >
                <config.links.help.FormattedMessage />
              </a>
            </>

            <>
              <span className="inline-block border-l" aria-hidden="true" />
              <a
                href={config.links.support.href}
                className="text-sm font-medium text-muted-foreground hover:underline"
              >
                <config.links.support.FormattedMessage />
              </a>
            </>
          </HStack>
        </footer>
      </div>
    );
  }
}

export default ErrorBoundary;
